import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkSmallPriceTagIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/price-tag';
import BpkImage, {
  withLazyLoading,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import {
  BpkBox,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkPrice, {
  SIZES,
  ALIGNS,
} from '@skyscanner-internal/backpack-web/bpk-component-price';
import BpkRating, {
  RATING_SIZES,
  RATING_SCALES,
} from '@skyscanner-internal/backpack-web/bpk-component-rating';
import BpkStarRating from '@skyscanner-internal/backpack-web/bpk-component-star-rating';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
  WHITE_SPACE,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import { useMBDContext } from '../common/MBD/useMBDContext';

import type { InventoryCardProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/InventoryCardProps';

import STYLES from './InventoryCard.module.scss';

const documentIfExists = typeof window !== 'undefined' ? document : null;
const LazyLoadedImage = withLazyLoading(BpkImage, documentIfExists);
const IMG_WIDTH = 392;
const IMG_HEIGHT = 116;

const ratingDetails = (rating: {
  ratingKey?: string | undefined;
  title?: string | undefined;
  tripAdvisorScoreImageUrl: any;
  value: any;
  label?: string;
  subtitle?: string;
}) => {
  if (rating.ratingKey) {
    return rating.ratingKey;
  }
  return null;
};

const InventoryCard = ({
  blankTarget = false,
  discountInfo,
  distanceFromCentre,
  hotelStars,
  hotelStarsAltText,
  href,
  imageAltText,
  imageSrc,
  index,
  mbdParams = {},
  noFollow = false,
  onClick,
  previousPriceText,
  price,
  priceLeadingText,
  priceTrailingText,
  rating,
  showHeartPOC = false,
  title,
}: InventoryCardProps & {
  index: number | string;
  showHeartPOC?: boolean;
  mbdParams?: any;
}) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const mbdName = showHeartPOC
    ? `${mbdParams.component_name}_item`
    : `${rawComponentName}_item`;

  const imageClassName = classNames(STYLES.hotelImage, {
    [STYLES.hotelImagePOC]: showHeartPOC,
  });

  const rel =
    (showHeartPOC && 'noopener noreferrer') ||
    (noFollow && 'nofollow') ||
    undefined;
  return (
    // BpkVessel (not BpkBox/BpkFlex): `.InventoryCard` (on the BpkCard below)
    // is nested under `.InventoryCardWrapper` without `&`, compiling to the
    // compound selector `.InventoryCardWrapper .InventoryCard` that requires
    // this className to remain in the DOM as an ancestor. `.InventoryCard`
    // itself can't drop its className anyway: `$card-margin:
    // bpk-spacing-base() * 0.75` = 0.75rem, and BpkMarginValue only accepts
    // spacing tokens/percentages/auto, not arbitrary rem (verified against
    // tokens.js isValidMarginValue — no token matches 0.75rem). See
    // backpack-api-gaps/WOM-6777-themecard-inventorycard.md.
    <BpkVessel as="div" className={STYLES.InventoryCardWrapper}>
      <BpkCard
        rel={rel}
        padded={false}
        className={STYLES.InventoryCard}
        href={href}
        blank={showHeartPOC ? true : blankTarget}
        onClick={onClick}
        data-tracking-element-id={`${mbdName}_${index}`}
        data-tracking-common-params={
          showHeartPOC ? JSON.stringify(mbdParams) : buildMBDParam(mbdName)
        }
      >
        {/* BpkVessel (not BpkBox/BpkFlex): needs `border-radius` (top
            corners only — typed `never`) plus a descendant selector into
            BpkImage's own internal `img`. See
            backpack-api-gaps/WOM-6777-themecard-inventorycard.md. */}
        <BpkVessel as="div" className={imageClassName}>
          <LazyLoadedImage
            aspectRatio={showHeartPOC ? 2 : IMG_WIDTH / IMG_HEIGHT}
            altText={imageAltText}
            src={imageSrc?.replace('_WxH', `_${IMG_WIDTH}x${IMG_HEIGHT}`)}
          />
        </BpkVessel>
        <BpkBox>
          <BpkBox
            height="6.75rem"
            paddingTop={BpkSpacing.Base}
            paddingStart={BpkSpacing.Base}
            paddingEnd={BpkSpacing.Base}
          >
            <BpkFlex justify="space-between">
              <BpkText
                color={TEXT_COLORS.textPrimary}
                className={STYLES.hotelTitle}
                tagName="span"
                textStyle={TEXT_STYLES.heading4}
                whiteSpace={WHITE_SPACE.nowrap}
              >
                {title}
              </BpkText>
              {hotelStars > 0 && (
                // Preserve the rating's full width so its inline stars cannot wrap.
                <BpkBox flexShrink={0}>
                  <BpkStarRating
                    className={STYLES.starRating}
                    ratingLabel={hotelStarsAltText}
                    rating={hotelStars}
                  />
                </BpkBox>
              )}
            </BpkFlex>
            {distanceFromCentre && (
              <BpkBox
                marginBottom={BpkSpacing.MD}
                color="text-primary"
                textAlign="start"
              >
                <BpkText tagName="span" textStyle={TEXT_STYLES.footnote}>
                  {distanceFromCentre}
                </BpkText>
              </BpkBox>
            )}
            {rating?.reviewCount !== 0 && (
              <BpkBox>
                <BpkRating
                  className={STYLES.tripAdvisorRating}
                  ariaLabel={rating.label}
                  value={rating.value}
                  title={ratingDetails(rating)}
                  subtitle={rating.subtitle}
                  size={RATING_SIZES.base}
                  ratingScale={RATING_SCALES.zeroToFive}
                  showScale
                />
              </BpkBox>
            )}
          </BpkBox>
          {/* BpkVessel (not BpkBox/BpkFlex): needs `border-top`/`box-shadow`
              (typed `never`) and a `::after` pseudo-element override, none
              of which BpkBox/BpkFlex expose. See
              backpack-api-gaps/WOM-6777-themecard-inventorycard.md. */}
          <BpkVessel as="div" className={STYLES.hotelDateAndPrice}>
            {/* BpkVessel (not BpkBox/BpkFlex): needs physical `text-align:
                right`. BpkBox's `textAlign` only accepts logical
                `start`/`end`/`center`/`justify` — `end` would flip to the
                left in RTL, changing this element's current (non-RTL-aware)
                behaviour. See
                backpack-api-gaps/WOM-6777-themecard-inventorycard.md. */}
            <BpkVessel as="div" className={STYLES.hotelPrice}>
              {price ? (
                <BpkPrice
                  className={STYLES.price}
                  price={price}
                  size={SIZES.small}
                  align={ALIGNS.right}
                  previousPrice={previousPriceText}
                  trailingText={priceTrailingText}
                  leadingText={priceLeadingText}
                />
              ) : null}
            </BpkVessel>
            {discountInfo ? (
              <BpkFlex
                direction="column"
                align="flex-start"
                gap={BpkSpacing.SM}
              >
                {discountInfo.discountPercentageOff && (
                  <BpkBadge
                    type={BADGE_TYPES.brand}
                    className={STYLES.CugBadge__discount}
                  >
                    <BpkSmallPriceTagIcon className={STYLES.CugBadge__icon} />
                    {discountInfo.discountPercentageOff}
                  </BpkBadge>
                )}
                {discountInfo.checkInAndCheckOutDate && (
                  <BpkText
                    textStyle={TEXT_STYLES.label2}
                    className={STYLES.CardContent__dates}
                    color={TEXT_COLORS.textPrimary}
                  >
                    {discountInfo?.checkInAndCheckOutDate}
                  </BpkText>
                )}
              </BpkFlex>
            ) : null}
          </BpkVessel>
        </BpkBox>
      </BpkCard>
    </BpkVessel>
  );
};
export default InventoryCard;
